/**
 * Turn-by-turn engine. Converts a stream of GPS fixes into navigation progress:
 * which step we're on, distance to the next maneuver, which lanes to be in,
 * and whether we've left the route.
 *
 * Deliberately has no DOM or map dependencies so its behaviour can be reasoned
 * about (and driven from a replayed track) in isolation.
 */

import type {
  BannerInstruction,
  Fix,
  Lane,
  LaneIndication,
  NavProgress,
  Route,
  VoiceInstruction,
} from '../core/types';
import { RouteIndex } from './routeIndex';

/** Deviation beyond this, sustained, means we're off route. */
const OFF_ROUTE_METRES = 45;
/** Consecutive off-route fixes before we commit to rerouting. Rejects blips. */
const OFF_ROUTE_FIXES = 4;
/** Within this distance of the final waypoint we call it arrived. */
const ARRIVAL_METRES = 30;

export interface NavigatorEvents {
  onProgress?: (p: NavProgress) => void;
  onAnnounce?: (text: string) => void;
  onOffRoute?: () => void;
  onArrive?: () => void;
  onStepChange?: (p: NavProgress) => void;
}

export class RouteNavigator {
  private index: RouteIndex;
  private lastVertex = 0;
  private currentStep = 0;
  private offRouteStreak = 0;
  private spokenVoice = new Set<string>();
  private arrived = false;
  /** Distance along route at the previous fix, used to reject backwards jitter. */
  private lastAlong = 0;

  constructor(
    route: Route,
    private events: NavigatorEvents = {},
  ) {
    this.index = new RouteIndex(route);
  }

  get route(): Route {
    return this.index.route;
  }

  /** Swap in a fresh route after a reroute, preserving nothing. */
  replaceRoute(route: Route): void {
    this.index = new RouteIndex(route);
    this.lastVertex = 0;
    this.currentStep = 0;
    this.offRouteStreak = 0;
    this.lastAlong = 0;
    this.arrived = false;
    this.spokenVoice.clear();
  }

  update(fix: Fix): NavProgress {
    const snapped = this.index.snap([fix.lng, fix.lat], this.lastVertex);
    this.lastVertex = snapped.segmentIndex;

    // GPS jitter can nudge us backwards; ignore small regressions so the
    // distance-to-turn readout never counts up while we're driving at it.
    let along = snapped.distanceAlongRoute;
    if (along < this.lastAlong && this.lastAlong - along < 25) {
      along = this.lastAlong;
    }
    this.lastAlong = along;

    const stepIdx = this.index.stepAt(along, this.currentStep);
    const stepChanged = stepIdx !== this.currentStep;
    this.currentStep = stepIdx;

    const indexed = this.index.steps[stepIdx];
    const nextIndexed = this.index.steps[stepIdx + 1] ?? null;

    const distanceToManeuver = Math.max(0, indexed.endDistance - along);
    const distanceRemaining = Math.max(0, this.index.totalDistance - along);

    // Scale remaining time by how much of the route is left. Cruder than
    // re-requesting an ETA, but free and stable; the traffic-aware total from
    // the API is still the basis.
    const fraction = this.index.totalDistance > 0 ? distanceRemaining / this.index.totalDistance : 0;
    const durationRemaining = this.index.route.duration * fraction;

    // Off-route: require a run of bad fixes, and don't trip on a wide accuracy
    // circle (a 60 m accuracy fix is not evidence of leaving the road). While
    // stationary, GPS drift produces exactly this pattern - a parked car must
    // never trigger the reroute loop, so demand actual movement unless the
    // deviation is far beyond drift range.
    const tolerance = OFF_ROUTE_METRES + Math.min(40, Math.max(0, fix.accuracy - 10));
    const trustworthy = fix.accuracy <= 60;
    const moving = fix.speed == null || fix.speed > 1.5;
    if (snapped.deviation > tolerance && trustworthy && (moving || snapped.deviation > 150)) {
      this.offRouteStreak++;
    } else if (snapped.deviation <= tolerance) {
      this.offRouteStreak = 0;
    }
    const offRoute = this.offRouteStreak >= OFF_ROUTE_FIXES;

    const banner = pickBanner(indexed.step.bannerInstructions, distanceToManeuver);
    const lanes = pickLanes(banner, indexed.step, distanceToManeuver);

    const progress: NavProgress = {
      legIndex: indexed.legIndex,
      stepIndex: indexed.stepIndex,
      distanceToManeuver,
      distanceRemaining,
      durationRemaining,
      step: indexed.step,
      nextStep: nextIndexed?.step ?? null,
      banner,
      lanes,
      snapped,
      offRoute,
    };

    this.speakIfDue(indexed.step.voiceInstructions, distanceToManeuver, stepIdx);

    if (stepChanged) this.events.onStepChange?.(progress);
    this.events.onProgress?.(progress);

    if (offRoute) {
      this.offRouteStreak = 0; // let the caller decide; don't spam
      this.events.onOffRoute?.();
    }

    if (!this.arrived && distanceRemaining <= ARRIVAL_METRES) {
      this.arrived = true;
      this.events.onArrive?.();
    }

    return progress;
  }

  private speakIfDue(
    instructions: VoiceInstruction[] | undefined,
    distanceToManeuver: number,
    stepIdx: number,
  ): void {
    if (!instructions?.length) return;
    for (let i = 0; i < instructions.length; i++) {
      const vi = instructions[i];
      if (distanceToManeuver > vi.distanceAlongGeometry) continue;
      const key = `${stepIdx}:${i}`;
      if (this.spokenVoice.has(key)) continue;
      // Mark every earlier announcement for this step as spoken too, so that
      // resuming mid-step doesn't replay a backlog of "in 800 metres...".
      for (let j = 0; j <= i; j++) this.spokenVoice.add(`${stepIdx}:${j}`);
      this.events.onAnnounce?.(vi.announcement);
      break;
    }
  }
}

/**
 * Choose the banner in force. Mapbox gives several per step, each with the
 * distance at which it should *start* showing; the correct one is the most
 * specific (smallest trigger distance) that we're already inside.
 */
export function pickBanner(
  banners: BannerInstruction[] | undefined,
  distanceToManeuver: number,
): BannerInstruction | null {
  if (!banners?.length) return null;
  let best: BannerInstruction | null = null;
  for (const b of banners) {
    if (distanceToManeuver <= b.distanceAlongGeometry) {
      if (!best || b.distanceAlongGeometry < best.distanceAlongGeometry) best = b;
    }
  }
  return best ?? banners.reduce((a, b) => (a.distanceAlongGeometry > b.distanceAlongGeometry ? a : b));
}

/**
 * Lane model for the lane bar.
 *
 * Two sources exist and they disagree in useful ways:
 *  - `banner.sub.components` is what Mapbox's own nav UI draws, already filtered
 *    to the upcoming maneuver. Preferred when present.
 *  - `intersections[].lanes` is the raw road model, available further out.
 *
 * We only surface lanes when the maneuver is close enough to act on - showing
 * lane arrows two kilometres early is noise.
 */
export function pickLanes(
  banner: BannerInstruction | null,
  step: { intersections?: Array<{ lanes?: Lane[] }> },
  distanceToManeuver: number,
): Lane[] | null {
  if (distanceToManeuver > 900) return null;

  const sub = banner?.sub;
  if (sub?.components?.length) {
    const laneComponents = sub.components.filter((c) => c.type === 'lane');
    if (laneComponents.length) {
      return laneComponents.map((c) => ({
        valid: c.active === true,
        active: c.active === true,
        valid_indication: c.active_direction,
        indications: (c.directions ?? []) as LaneIndication[],
      }));
    }
  }

  // Fall back to the last intersection that carries lane data - that's the one
  // immediately before the turn.
  const withLanes = (step.intersections ?? []).filter((i) => i.lanes?.length);
  const last = withLanes[withLanes.length - 1];
  return last?.lanes ?? null;
}

export { OFF_ROUTE_METRES, ARRIVAL_METRES };
